/**
 * liveEventBridge.js — Lightweight in-process pub/sub bus for Kevia events.
 *
 * Allows instant cache invalidation and context refresh without waiting
 * for the 5-minute TTL to expire. Zero external dependencies.
 *
 * Usage:
 *   const unsub = onKeviaEvent((event) => { ... });
 *   emitKeviaEvent(KEVIA_EVENTS.MUTATION_SUCCESS, { toolId });
 *   // cleanup:
 *   unsub();
 */

/* ─── Event type registry ─────────────────────────────────────── */

export const KEVIA_EVENTS = Object.freeze({
  /** Fired after a confirmed mutation executes successfully. */
  MUTATION_SUCCESS: "mutation_success",
  /** Fired when a new entity context is registered (route/entity changed). */
  ENTITY_OPENED:   "entity_opened",
  /** External signal that the operational snapshot is stale. */
  SNAPSHOT_STALE:  "snapshot_stale",
  /** Significant route change (new module, not just param change). */
  ROUTE_CHANGED:   "route_changed",
});

/* ─── Internal state ─────────────────────────────────────────── */

const _handlers = new Set();

/* ─── Public API ─────────────────────────────────────────────── */

/**
 * Subscribe to all Kevia bus events.
 * @param {(event: { type: string, payload: object, ts: number }) => void} handler
 * @returns {() => void} unsubscribe function
 */
export function onKeviaEvent(handler) {
  _handlers.add(handler);
  return () => _handlers.delete(handler);
}

/**
 * Emit an event to all subscribed handlers.
 * Errors in individual handlers are caught and suppressed.
 *
 * @param {string} type — one of KEVIA_EVENTS
 * @param {object} [payload]
 */
export function emitKeviaEvent(type, payload = {}) {
  const event = { type, payload, ts: Date.now() };
  for (const handler of _handlers) {
    try { handler(event); } catch { /* subscriber errors must not break the bus */ }
  }
}

/**
 * Remove all event listeners. Called on component unmount to prevent leaks.
 * Note: prefer per-handler unsub returned by onKeviaEvent in components.
 * This is for test teardown / full reset scenarios.
 */
export function clearKeviaEventListeners() {
  _handlers.clear();
}
